// Customizable Area Start
import { IBlock } from "../../../framework/src/IBlock";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import React from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import moment from "moment";
import {
  User,
  IncomingMessage,
  IncomingMediaMessage,
  IncomingTextMessage,
  Receipt,
  CometCall,
} from "./ChatController";
import { CustomContext } from "../../../components/src/CustomContext.web";
import { InfoType } from "../../../components/src/CustomInfoDialog.web";
import emitter from "../../../components/src/eventBus.web"
import { navigateToUserProfile } from "../../../components/src/utils/userSlug.web";
import { logEvent } from "../../../components/src/utils/googleAnalytics/analyticsAdapter.web";
export const configJSON = require("./config");

const DETAILS_PRESENCE_LISTENER = "DETAILS_PRESENCE_LISTENER";
const DETAILS_MESSAGE_LISTENER = "DETAILS_MESSAGE_LISTENER";
const DETAILS_CALL_LISTENER_ID = "DETAILS_CALL_LISTENER_ID"
// Customizable Area End

// Customizable Area Start
export type Emoji = {
  id: string;
  name: string;
  native: string;
  unified: string;
  shortcodes: string[];
  keywords: string[];
  skins: unknown[];
  category: string;
};
// Customizable Area End

export interface Props {
  // Customizable Area Start
  navigation: any;
  selectedChatUID: string;
  markReadMessagesCon: (conId: string) => void;
  toggleChatSelection: (chatUID: string) => void;
  handleRemoveConversation: (conId: string) => void;
  updateConversationByMessage: (message: IncomingMessage) => void;
  // Customizable Area End
}

interface S {
  // Customizable Area Start
  chats: IncomingMessage[];
  isLoadingMessages: boolean;
  recipientInfo: User | null;
  message: string;
  loggedInUserId: string;
  attachments: File[];
  isTyping: boolean;
  isInfoDialog: boolean;
  infoType: InfoType;
  isDeletingChats: boolean;
  isDeleteChatDialog: boolean;
  isBlockChatDialog: boolean;
  isSendingMessages: boolean;
  isCallDialog: boolean;
  isUnBlockChatDialog: boolean;
  isUnblockingUser: boolean;
  isBlockingUser: boolean;
  isVideoCall: boolean
  cometCall: CometCall | null;
  chatOptionsAnchorEl: HTMLButtonElement | null;
  emojiAnchorEl: HTMLButtonElement | null;
  // Customizable Area End
}

interface SS {
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

export default class ChatDetailsController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  typingTimeout: number | null = null;
  messageRequest: CometChat.MessagesRequest | null = null;
  messagesEndRef: React.RefObject<HTMLDivElement> = React.createRef();
  inputFieldRef: React.RefObject<HTMLInputElement> = React.createRef();
  messageFieldRef: React.RefObject<HTMLInputElement> = React.createRef();
  static contextType = CustomContext;
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.receive = this.receive.bind(this);

    this.subScribedMessages = [
      // Customizable Area Start
      getName(MessageEnum.SessionResponseMessage),
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.NavigationPayLoadMessage),
      // Customizable Area End
    ];

    this.state = {
      // Customizable Area Start
      chats: [],
      isLoadingMessages: false,
      message: "",
      loggedInUserId: "",
      attachments: [],
      isTyping: false,
      isInfoDialog: false,
      infoType: "success",
      recipientInfo: null,
      chatOptionsAnchorEl: null,
      emojiAnchorEl: null,
      isDeletingChats: false,
      isDeleteChatDialog: false,
      isBlockChatDialog: false,
      isUnBlockChatDialog: false,
      isSendingMessages: false,
      isCallDialog: false,
      isUnblockingUser: false,
      isBlockingUser: false,
      cometCall: null,
      isVideoCall: false
      // Customizable Area End
    };
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  get customContext(): React.ContextType<typeof CustomContext> {
    return this.context as React.ContextType<typeof CustomContext>;
  }

  async componentDidMount(): Promise<void> {
    if (this.props.selectedChatUID) {
      this.handlePageLoadCalls();
    }
  }

  async componentDidUpdate(prevProps: Readonly<Props>): Promise<void> {
    if (this.props.selectedChatUID !== prevProps.selectedChatUID) {
      this.handlePageLoadCalls();
    }
  }

  async componentWillUnmount() {
    this.messagesEndRef.current?.removeEventListener(
      "scroll",
      this.handleScroll
    );
    this.removeCometChatListeners();
  }

  eventHandler = ({ call }: { call: CometCall }) => {
    this.handleIncomingCallLogs(call)
  }

  handlePageLoadCalls = () => {
    const { selectedChatUID } = this.props;

    this.handleResetChat();
    if (selectedChatUID) {
      this.messageRequest = new CometChat.MessagesRequestBuilder()
        .setUID(selectedChatUID)
        .hideDeletedMessages(true)
        .setLimit(30)
        .build();

      this.fetchUserDetails();
      this.fetchChatMessages();
      this.addCometChatListeners();
      emitter.on('incoming-call', this.eventHandler)
    }
  };

  getChatMenus = () => {
    return [
      {
        title: "View profile",
        icon: <PersonOutlineRoundedIcon />,
        handleClick: this.handleProfilePageRedirect,
      },
      {
        title: "Block chat",
        icon: <BlockRoundedIcon />,
        isDisabled: this.state.recipientInfo?.blockedByMe,
        handleClick: this.handleOpenBlockChatDialog,
      },
      {
        title: "Delete chat",
        icon: <DeleteOutlineRoundedIcon />,
        handleClick: this.handleOpenDeleteChatDialog,
      },
    ];
  };

  loadPreviousMessages = async () => {
    if (!this.messageRequest) return;

    this.setState({ isLoadingMessages: true });

    try {
      const prevScrollHeight = this.messagesEndRef.current?.scrollHeight || 0;

      const olderMessages = await this.messageRequest.fetchPrevious();

      this.setState(
        (prevState) => ({
          chats: [
            ...((olderMessages as unknown) as IncomingMessage[]),
            ...prevState.chats,
          ],
          isLoadingMessages: false,
        }),
        () => {
          const newScrollHeight =
            this.messagesEndRef.current?.scrollHeight || 0;
          const scrollDiff = newScrollHeight - prevScrollHeight;

          if (this.messagesEndRef.current) {
            this.messagesEndRef.current.scrollTop = scrollDiff;
          }
        }
      );
    } catch (error) {
      console.error("Failed to load older messages:", error);
      this.setState({ isLoadingMessages: false });
    }
  };

  fetchUserDetails = async () => {
    const { selectedChatUID } = this.props;
    try {
      const loggedInUser = await CometChat.getLoggedinUser();
      const user = await CometChat.getUser(selectedChatUID) as unknown as User;
      logEvent('start_chat', { recipient_id: user.uid, recipient_name: user.name })
      this.setState({
        recipientInfo: (user as unknown) as User,
        loggedInUserId: ((loggedInUser as unknown) as User).uid,
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  handleScroll = () => {
    const container = this.messagesEndRef.current;
    if (!container || this.state.isLoadingMessages) return;

    if (container.scrollTop <= 50) {
      this.loadPreviousMessages();
    }
  };

  handleResetChat = () => {
    this.setState({
      recipientInfo: null,
      chats: [],
      attachments: [],
      message: "",
      chatOptionsAnchorEl: null,
      emojiAnchorEl: null,
    });
    this.removeCometChatListeners();
    emitter.off('incoming-call', this.eventHandler)
  };

  handleUserUpdate = (user: User) => {
    const { recipientInfo } = this.state;

    if (user.uid === (recipientInfo as User).uid) {
      this.setState({ recipientInfo: user });
    }
  };

  handleRecipientStartTyping = (typingIndicator: { sender: User }) => {
    if (typingIndicator.sender.uid === this.state.recipientInfo?.uid) {
      this.setState({ isTyping: true });
    }
  };

  handleRecipientSeenMessage = (readReceipt: Receipt) => {
    const { readAt } = readReceipt;
    const updatedChats = this.state.chats.map((chat) => {
      chat.readAt = readAt;
      return chat;
    });
    this.setState({ chats: updatedChats });
  };

  handleRecipientStopTyping = (typingIndicator: { sender: User }) => {
    if (typingIndicator.sender.uid === this.state.recipientInfo?.uid) {
      this.setState({ isTyping: false });
    }
  };

  handleIncomingCallLogs = (call: CometCall) => {
    const { recipientInfo } = this.state;
    const { callInitiator } = call;
    if (recipientInfo && recipientInfo.uid === callInitiator.uid) {
      this.handleUpdateCallMsg(call);
    }
  }

  scrollToMessagesBottom = (isSmooth?: boolean) => {
    const container = this.messagesEndRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: isSmooth ? "smooth" : "auto",
      });
    }
  };

  scrollIfUserNearBottom = () => {
    const element = this.messagesEndRef.current;
    if (!element) return false;

    const threshold = 150;
    if (
      element.scrollHeight - element.scrollTop - element.clientHeight <
      threshold
    ) {
      this.scrollToMessagesBottom(true);
    }
  };

  handleIncomingMessage = (message: IncomingMessage) => {
    const { recipientInfo } = this.state;
    const { sender } = message;

    const isFromRecipient = sender.uid === recipientInfo?.uid;

    if (!isFromRecipient) return;
    CometChat.markAsRead(message);

    this.setState(
      (prev) => ({
        chats: [...prev.chats, message],
      }),
      () => this.scrollIfUserNearBottom()
    );
  };

  addCometChatListeners = () => {
    CometChat.addUserListener(
      DETAILS_PRESENCE_LISTENER,
      new CometChat.UserListener({
        onUserOnline: this.handleUserUpdate,
        onUserOffline: this.handleUserUpdate,
      })
    );

    CometChat.addMessageListener(
      DETAILS_MESSAGE_LISTENER,
      new CometChat.MessageListener({
        onTextMessageReceived: this.handleIncomingMessage,
        onMediaMessageReceived: this.handleIncomingMessage,
        onCustomMessageReceived: this.handleIncomingMessage,
        onTypingStarted: this.handleRecipientStartTyping,
        onTypingEnded: this.handleRecipientStopTyping,
        onMessagesRead: this.handleRecipientSeenMessage,
      })
    );

    CometChat.addCallListener(
      DETAILS_CALL_LISTENER_ID,
      new CometChat.CallListener({
        onIncomingCallReceived: this.handleIncomingCallLogs,
        onIncomingCallCancelled: this.handleIncomingCallLogs,
        onCallEnded: this.handleIncomingCallLogs
      })
    );
  };

  removeCometChatListeners = () => {
    CometChat.removeUserListener(DETAILS_PRESENCE_LISTENER);
    CometChat.removeMessageListener(DETAILS_MESSAGE_LISTENER);
    CometChat.removeCallListener(DETAILS_CALL_LISTENER_ID)
  };

  fetchChatMessages = async () => {
    try {
      this.setState({ isLoadingMessages: true });

      const messages = await (this
        .messageRequest as CometChat.MessagesRequest).fetchPrevious()
      const lastMessage = (messages[
        messages.length - 1
      ] as unknown) as IncomingMessage;
      if (lastMessage && lastMessage.sender.uid !== this.state.loggedInUserId) {
        this.props.markReadMessagesCon(lastMessage.conversationId);
        const unreadMessages = await CometChat.getUnreadMessageCountForAllUsers(
          true
        );
        const totalCount = Object.values(unreadMessages).reduce(
          (sum, count) => sum + count,
          0
        );
        CometChat.markAsRead(lastMessage);
        if (`${Number(lastMessage.sender.uid)}` in unreadMessages) {
          this.customContext.setUnreadCount(
            (totalCount -
              ((unreadMessages as unknown) as Record<string, number>)[
                lastMessage.sender.uid
              ])
          );
        }
      }
      this.setState({
        chats: messages as IncomingMessage[],
        isLoadingMessages: false,
      });
      this.scrollToMessagesBottom();
      if (this.messagesEndRef.current) {
        this.messagesEndRef.current.addEventListener(
          "scroll",
          this.handleScroll
        );
      }
    } catch (error) {
      console.error("Fetching messages failed:", error);
    }
  };

  handleAddAttachment = () => {
    if (this.inputFieldRef.current) {
      this.inputFieldRef.current.click();
    }
  };

  handleTypingIndicator = (isStarted: boolean = false) => {
    const receiver = this.state.recipientInfo as User;
    const receiverType = CometChat.RECEIVER_TYPE.USER;

    const typingNotification = new CometChat.TypingIndicator(
      receiver.uid,
      receiverType
    );
    if (isStarted) CometChat.startTyping(typingNotification);
    else CometChat.endTyping(typingNotification);
  };

  handleMessageValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.setState({ message: value });
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    this.handleTypingIndicator(true);

    this.typingTimeout = window.setTimeout(() => {
      this.handleTypingIndicator();
    }, 700);
  };

  sendMessage = async ({
    recipientInfo,
    messageText,
    files,
  }: {
    recipientInfo: User;
    messageText?: string;
    files: File[];
  }): Promise<IncomingMessage[]> => {
    const sentMessages: IncomingMessage[] = [];

    try {
      if (files && files.length > 0) {
        for (const file of Array.from(files)) {
          let type: string;

          if (file.type.startsWith("image")) {
            type = CometChat.MESSAGE_TYPE.IMAGE;
          } else if (file.type.startsWith("video")) {
            type = CometChat.MESSAGE_TYPE.VIDEO;
          } else {
            type = CometChat.MESSAGE_TYPE.FILE;
          }

          logEvent("send_message", {
            recipient_id: recipientInfo.uid,
            recipient_name: recipientInfo.name,
            message_type: type
          });
          const mediaMessage = new CometChat.MediaMessage(
            recipientInfo.uid,
            file,
            type,
            CometChat.RECEIVER_TYPE.USER
          );

          const mediaResponse = await CometChat.sendMessage(mediaMessage);
          sentMessages.push((mediaResponse as unknown) as IncomingMediaMessage);
        }
      }

      if (messageText && messageText.trim()) {
        const textMessage = new CometChat.TextMessage(
          recipientInfo.uid,
          messageText.trim(),
          CometChat.RECEIVER_TYPE.USER
        );

        const textResponse = await CometChat.sendMessage(textMessage);

        sentMessages.push((textResponse as unknown) as IncomingTextMessage);
      }

      return sentMessages;
    } catch (error) {
      this.setState({ isSendingMessages: false });
      throw error;
    }
  };

  handleSendMessage = async () => {
    const { recipientInfo, message, attachments } = this.state;
    if (recipientInfo) {
      this.setState({ isSendingMessages: true });
      try {
        const messages = await this.sendMessage({
          recipientInfo,
          messageText: message,
          files: attachments,
        });

        this.setState(
          (prevState) => ({
            chats: [...prevState.chats, ...messages],
            message: "",
            attachments: [],
            isSendingMessages: false,
          }),
          () => {
            this.messageFieldRef.current?.focus()
            this.scrollToMessagesBottom();
            this.props.updateConversationByMessage(
              messages[messages.length - 1]
            );
          }
        );
      } catch (err) {
        this.handleOpenInfoDialog("error");
      }
    }
  };

  handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files) return;

    const validFiles: File[] = [];

    Array.from(files).forEach((file) => {
      if (configJSON.AllowedMimeTypes.includes(file.type)) {
        validFiles.push(file);
      }
    });

    if (validFiles.length > 0) {
      this.setState((prevState) => {
        return {
          attachments: [...prevState.attachments, ...validFiles],
        };
      });
    }
  };

  removeAttachment = (indexToRemove: number) => {
    const updated = Array.from(this.state.attachments || []).filter(
      (_, index) => index !== indexToRemove
    );
    this.setState({ attachments: updated });
  };

  handleBlockUser = async () => {
    const { recipientInfo } = this.state;
    if (recipientInfo) {
      try {
        this.setState({ isBlockingUser: true });
        await CometChat.blockUsers([recipientInfo.uid]);
        this.setState({
          isBlockChatDialog: false,
          isBlockingUser: false,
          chatOptionsAnchorEl: null,
          recipientInfo: {
            ...recipientInfo,
            blockedByMe: true,
          },
        });
        this.scrollToMessagesBottom();
      } catch (_error) {
        this.setState({ isBlockingUser: false, isBlockChatDialog: false });
        this.handleOpenInfoDialog("error");
      }
    }
  };

  handleUnblockUser = async () => {
    const { recipientInfo } = this.state;
    if (recipientInfo) {
      try {
        this.setState({ isUnblockingUser: true });
        await CometChat.unblockUsers([recipientInfo.uid]);
        this.setState({
          isUnBlockChatDialog: false,
          isUnblockingUser: false,
          recipientInfo: {
            ...recipientInfo,
            blockedByMe: false,
          },
        });
      } catch (_error) {
        this.setState({ isUnblockingUser: false, isUnBlockChatDialog: false });
        this.handleOpenInfoDialog("error");
      }
    }
  };

  handleDeleteChats = async () => {
    const { recipientInfo } = this.state;
    if (recipientInfo) {
      try {
        this.setState({ isDeletingChats: true });
        await CometChat.deleteConversation(
          recipientInfo.uid,
          CometChat.RECEIVER_TYPE.USER
        );
        this.setState({ isDeletingChats: false, isDeleteChatDialog: false });
        this.customContext.setSelectedChatUID("");
        this.props.handleRemoveConversation(recipientInfo.conversationId);
      } catch (_error) {
        this.setState({ isDeletingChats: false, isDeleteChatDialog: false });
        this.handleOpenInfoDialog("error");
      }
    }
  };

  handleEmojiSelect = (emoji: Emoji) => {
    this.setState((prevState) => {
      return {
        message: `${prevState.message}${emoji.native}`,
      };
    });
  };

  handleMessageKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      this.handleSendMessage();
    }
  };

  getChatsByDate = (chats: IncomingMessage[]) => {
    return chats.reduce((acc, chat) => {
      const dateKey = moment(chat.sentAt * 1000).format("DD/MM/YYYY");
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(chat);
      return acc;
    }, {} as Record<string, IncomingMessage[]>);
  };

  handleChatOptionsPopupClose = () => {
    this.setState({ chatOptionsAnchorEl: null });
  };

  handleChatOptionsPopupClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    this.setState({ chatOptionsAnchorEl: event.currentTarget });
  };

  handleEmojiPickerClose = () => {
    this.setState({ emojiAnchorEl: null });
  };

  handleEmojiPickerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    this.setState({ emojiAnchorEl: event.currentTarget });
  };

  handleOpenDeleteChatDialog = () => {
    this.setState({ isDeleteChatDialog: true });
  };

  handleCloseDeleteChatDialog = () => {
    this.setState({ isDeleteChatDialog: false });
  };

  handleOpenBlockChatDialog = () => {
    this.setState({ isBlockChatDialog: true });
  };

  handleCloseBlockChatDialog = () => {
    this.setState({ isBlockChatDialog: false });
  };

  handleOpenUnBlockChatDialog = () => {
    this.setState({ isUnBlockChatDialog: true });
  };

  handleCloseUnBlockChatDialog = () => {
    this.setState({ isUnBlockChatDialog: false });
  };

  handleOpenCallDialog = (isVideoCall: boolean) => {
    this.setState({ isCallDialog: true, isVideoCall });
  };

  handleCloseCallDialog = () => {
    this.setState({ isCallDialog: false, cometCall: null });
  };

  handleUpdateCallMsg = (callMsg: CometCall) => {
    this.setState(
      (prev) => ({
        chats: [...prev.chats, callMsg],
      }),
      () => this.scrollToMessagesBottom(true)
    );
  }

  handleOpenInfoDialog = (infoType: InfoType) => {
    this.setState({
      isInfoDialog: true,
      infoType,
    });
  };

  handleCloseInfoDialog = () => {
    this.setState({ isInfoDialog: false });
  };

  initiateCall = async (isVideoCall: boolean = false) => {
    this.handleOpenCallDialog(isVideoCall)
  };

  handleProfilePageRedirect = () => {
    const { recipientInfo } = this.state
    if(recipientInfo) {
      const { uid, name } = recipientInfo;
      const [firstName, lastName] = name.split(" ");
       navigateToUserProfile(this.props.navigation, {
        id: uid,
        firstName,
        lastName
      });
    }
  };
  // Customizable Area End
}
